import { firestoreAction } from "@/store/firestoreBinding";
import { db } from "@/firebase";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";
import { buildExtrapolationDoc, getExtrapolationFormulas, getFormulaGaugeIds } from "@/utils/flowFormulas";
import { LEVEL_FIELDS, withSectionLevels } from "@/utils/sectionLevels";

const sectionsRef = db.collection("sections");
const sectionsLevelHistoryRef = db.collection("sectionsLevelHistory");
// Current levels, members only (everyone for free_access sections): see utils/sectionLevels.js and firestore.rules
const sectionsLevelsRef = db.collection("sectionsLevels");
// Firestore "in" queries take at most 30 values
const IN_QUERY_LIMIT = 30;
const sectionsVideosRef = db.collection("sectionsVideos");
const sectionsToposRef = db.collection("sectionsTopos");
const sectionsDirectionsRef = db.collection("sectionsDirections");

function normalizeSectionClassLevels(section = {}) {
  const normalizedSection = { ...section };
  const hasClassLevel = Object.prototype.hasOwnProperty.call(normalizedSection, "class_level");
  const hasClassLevelMin = Object.prototype.hasOwnProperty.call(normalizedSection, "class_level_min");

  // Backward compatibility: legacy class_level represented min level.
  if (!hasClassLevelMin && hasClassLevel) {
    normalizedSection.class_level_min = normalizedSection.class_level;
  }

  // New schema: class_level is median/default level.
  if (!hasClassLevel && hasClassLevelMin) {
    normalizedSection.class_level = normalizedSection.class_level_min;
  }

  // Ensure median defaults to min while migration is in progress.
  if (
    hasClassLevelMin &&
    (normalizedSection.class_level === undefined ||
      normalizedSection.class_level === null ||
      normalizedSection.class_level === "")
  ) {
    normalizedSection.class_level = normalizedSection.class_level_min;
  }

  return normalizedSection;
}

// The section as saved by the admin: never with level fields, which live in sectionsLevels (members only).
// The admin edits docs loaded with getSections, which may still carry them from before.
function withoutLevelFields(section) {
  const sectionToSave = { ...section };
  LEVEL_FIELDS.forEach((field) => delete sectionToSave[field]);
  return sectionToSave;
}

function normalizeSectionPortages(section = {}) {
  const normalizedSection = { ...section };
  const hasPortage = Object.prototype.hasOwnProperty.call(normalizedSection, "portage");
  const hasPortageMin = Object.prototype.hasOwnProperty.call(normalizedSection, "portage_min");
  const hasPortageMax = Object.prototype.hasOwnProperty.call(normalizedSection, "portage_max");

  if (!hasPortage && hasPortageMin) {
    normalizedSection.portage = normalizedSection.portage_min;
  } else if (!hasPortage && hasPortageMax) {
    normalizedSection.portage = normalizedSection.portage_max;
  }

  delete normalizedSection.portage_min;
  delete normalizedSection.portage_max;

  return normalizedSection;
}

export default {
  namespaced: true,
  state: {
    sections: [], // Initialize sections as an empty array
    levels: [], // sectionsLevels docs the user may read
    levelsBoundForMembers: null, // null (not bound), true (all levels) or false (free_access levels only)
  },
  getters: {
    // Sections with their current level (when the user may read it), see withSectionLevels
    sections: (state) => {
      const levelsById = new Map((state.levels || []).map((levels) => [levels.id, levels]));
      return (state.sections || []).map((section) => withSectionLevels(section, levelsById.get(section.id)));
    },
  },
  mutations: {
    setLevelsBoundForMembers(state, value) {
      state.levelsBoundForMembers = value;
    },
  },
  actions: {
    bindSections: firestoreAction(({ bindFirestoreRef }) => {
      return bindFirestoreRef("sections", sectionsRef.orderBy("alias").where("published", "==", true));
    }),
    // Members read every level; the others must filter on free_access, or the rules refuse the whole query.
    // Bound again only when the membership changes.
    bindSectionLevels: firestoreAction(async ({ state, commit, bindFirestoreRef }, isMember) => {
      const forMembers = Boolean(isMember);
      if (state.levelsBoundForMembers === forMembers) return;
      commit("setLevelsBoundForMembers", forMembers);
      try {
        await bindFirestoreRef(
          "levels",
          forMembers ? sectionsLevelsRef : sectionsLevelsRef.where("free_access", "==", true),
        );
      } catch (error) {
        commit("setLevelsBoundForMembers", null);
        throw error;
      }
    }),
    bindSectionsAdmin: firestoreAction((context) => {
      context.bindFirestoreRef("sections", sectionsRef.orderBy("alias"));
    }),
    async getSections() {
      try {
        const snapshot = await sectionsRef.orderBy("alias").get();

        if (snapshot.empty) {
          return [];
        }

        return snapshot.docs.map((doc) => {
          return {
            ...normalizeSectionPortages(normalizeSectionClassLevels(doc.data())),
            id: doc.id,
          };
        });
      } catch (error) {
        console.error("Error fetching sections:", error);
        return [];
      }
    },
    querySectionsByLikedIds: (_context, likedIds) => {
      // likedIds will now be a batch of 10 or fewer IDs
      if (!likedIds || likedIds.length === 0) {
        return Promise.resolve({ docs: [] });
      }

      try {
        // This query will now always have 10 or fewer items in the 'in' clause
        return db.collection("sections").where(firebase.firestore.FieldPath.documentId(), "in", likedIds).get();
      } catch (error) {
        console.error("Error in querySectionsByLikedIds:", error);
        return Promise.reject(error);
      }
    },
    async findSectionsByAlias(sectionAlias) {
      if (typeof sectionAlias !== "string" || sectionAlias.trim() === "") {
        throw new Error("Invalid sectionAlias provided to findSectionsByAlias");
      }

      const snapshot = await sectionsRef.where("alias", "==", sectionAlias).get();
      if (snapshot.empty) {
        return [];
      }

      const sections = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      return sections;
    },
    // sectionsLevels doc of one section, or null when it doesn't exist or the user may not read it
    async findSectionLevels(id) {
      try {
        const doc = await sectionsLevelsRef.doc(id).get();
        return doc.exists ? doc.data() : null;
      } catch (error) {
        if (error?.code !== "permission-denied") console.error("Error fetching section levels:", error);
        return null;
      }
    },
    // sectionsLevels docs of some sections, by section id. Non-members only get the free_access ones.
    async findSectionLevelsByIds(ids, isMember) {
      const levelsById = {};
      const uniqueIds = [...new Set((ids || []).filter(Boolean))];

      // Non-members: one get per section. An "in" query is refused as a whole by the rules as soon as one of
      // the ids isn't free, even when filtering on free_access (checked on the emulator).
      if (!isMember) {
        const levels = await Promise.all(uniqueIds.map((id) => this.findSectionLevels(id)));
        uniqueIds.forEach((id, index) => {
          if (levels[index]) levelsById[id] = levels[index];
        });
        return levelsById;
      }

      const batches = [];
      for (let i = 0; i < uniqueIds.length; i += IN_QUERY_LIMIT) {
        batches.push(uniqueIds.slice(i, i + IN_QUERY_LIMIT));
      }
      try {
        const snapshots = await Promise.all(
          batches.map((batch) =>
            sectionsLevelsRef.where(firebase.firestore.FieldPath.documentId(), "in", batch).get(),
          ),
        );
        snapshots.forEach((snapshot) => snapshot.docs.forEach((doc) => (levelsById[doc.id] = doc.data())));
      } catch (error) {
        console.error("Error fetching section levels:", error);
      }
      return levelsById;
    },
    // Copies free_access to the level docs of the section, which the Firestore rules read.
    // The gauge Cloud Function also writes it on every run.
    async syncSectionLevelsAccess(sectionId, freeAccess) {
      const update = { free_access: freeAccess === true };
      await Promise.all(
        [sectionsLevelsRef, sectionsLevelHistoryRef].map((ref) =>
          ref
            .doc(sectionId)
            .update(update)
            .catch((error) => {
              // No level yet for this section: the Cloud Function creates the docs with free_access
              if (error?.code !== "not-found") throw error;
            }),
        ),
      );
    },
    async getExtrapolationBySectionId(id) {
      const extrapolationDoc = await sectionsRef.doc(id).collection("extrapolation").get();

      if (extrapolationDoc.empty) {
        return;
      }

      var extrapolationData = extrapolationDoc.docs.map((doc) => {
        return { ...doc.data(), id: doc.id };
      });

      return extrapolationData;
    },
    // Saves the formulas of the section (utils/flowFormulas.js). Returns the document id (new on a first save).
    async setExtrapolation(sectionId, formulas, primaryFormulaId, extrapolationId = null) {
      const collection = sectionsRef.doc(sectionId).collection("extrapolation");
      const docRef = extrapolationId ? collection.doc(extrapolationId) : collection.doc();
      await docRef.set(buildExtrapolationDoc(formulas, primaryFormulaId), { merge: true });
      return docRef.id;
    },
    async findSectionFlowHistory(id) {
      if (!id || typeof id !== "string") {
        throw new Error("Invalid section ID provided to findSectionFlowHistory");
      }

      const sectionsLevelHistoryDoc = sectionsLevelHistoryRef.doc(id);
      const doc = await sectionsLevelHistoryDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc;
    },
    async deleteSectionFlowHistory(sectionId) {
      const sectionLevelHistoryRefDelete = sectionsLevelHistoryRef.doc(sectionId);
      await sectionLevelHistoryRefDelete.delete();
    },
    async findSectionVideos(id) {
      const sectionsVideosDoc = sectionsVideosRef.doc(id);
      const doc = await sectionsVideosDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc.data();
    },
    async setSectionVideos(sectionId, videos) {
      const sectionsVideosDoc = sectionsVideosRef.doc(sectionId);

      if (videos) {
        sectionsVideosDoc.set({
          links: JSON.stringify(videos),
        });
      }
    },
    async deleteSectionVideos(sectionId) {
      const sectionVideosRefDelete = sectionsVideosRef.doc(sectionId);
      await sectionVideosRefDelete.delete();
    },
    async findSectionsTopos(id) {
      const sectionsToposDoc = sectionsToposRef.doc(id);
      const doc = await sectionsToposDoc.get();

      if (!doc.exists) {
        return null;
      }

      return doc.data();
    },
    async setSectionsTopos(sectionId, topos) {
      const sectionsToposDoc = sectionsToposRef.doc(sectionId);

      if (topos) {
        sectionsToposDoc.set({
          links: JSON.stringify(topos),
        });
      }
    },
    async deleteSectionsTopos(sectionId) {
      const sectionToposRefDelete = sectionsToposRef.doc(sectionId);
      await sectionToposRefDelete.delete();
    },
    async findSectionsDirections(id) {
      const sectionsDirectionsDoc = sectionsDirectionsRef.doc(id);
      const doc = await sectionsDirectionsDoc.get();

      if (!doc.exists) {
        return {
          markers: "[]",
          sectionPath: "[]",
        };
      }

      return doc.data();
    },
    async setSectionsDirections(sectionId, directions) {
      const sectionsDirectionsDoc = sectionsDirectionsRef.doc(sectionId);
      sectionsDirectionsDoc.set(
        {
          markers: directions.markers,
          sectionPath: directions.sectionPath,
        },
        { merge: true },
      );
    },
    async deleteSectionsDirections(sectionId) {
      const sectionDirectionsRefDelete = sectionsDirectionsRef.doc(sectionId);
      await sectionDirectionsRefDelete.delete();
    },
    async addSection(section) {
      const normalizedSection = withoutLevelFields(normalizeSectionPortages(normalizeSectionClassLevels(section)));
      normalizedSection.alias = this.createAlias(normalizedSection.name);
      const result = await sectionsRef.add(normalizedSection);
      console.log(result.id);
      return result.id;
    },
    async deleteSection(section) {
      await this.deleteSectionsDirections(section.id);
      await this.deleteSectionFlowHistory(section.id);
      await this.deleteSectionsTopos(section.id);
      await this.deleteSectionVideos(section.id);
      await sectionsLevelsRef.doc(section.id).delete();

      const sectionRefDelete = sectionsRef.doc(section.id);
      await sectionRefDelete.delete();
    },
    async updateSection(section) {
      const normalizedSection = withoutLevelFields(normalizeSectionPortages(normalizeSectionClassLevels(section)));
      normalizedSection.alias = this.createAlias(normalizedSection.river + " " + normalizedSection.name);
      const sectionRefUpdate = sectionsRef.doc(normalizedSection.id);
      await sectionRefUpdate.update(normalizedSection);
      await this.syncSectionLevelsAccess(normalizedSection.id, normalizedSection.free_access);
    },
    createAlias(stringToAlias) {
      stringToAlias = stringToAlias.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      return stringToAlias
        .replace(/[^a-z0-9\s]/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
    },
    async findSectionsByGaugeId(gaugeId) {
      if (!gaugeId || typeof gaugeId !== "string") {
        return [];
      }
      const sectionsSnapshot = await sectionsRef.orderBy("alias").get();
      if (sectionsSnapshot.empty) {
        return [];
      }
      const sections = sectionsSnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      const results = await Promise.all(
        sections.map(async (section) => {
          // Any formula of the section may use the gauge, not only the first term (legacy gauge_id)
          const extrapolationSnapshot = await sectionsRef.doc(section.id).collection("extrapolation").get();
          const usesGauge = extrapolationSnapshot.docs.some((doc) =>
            getFormulaGaugeIds(getExtrapolationFormulas(doc.data()).formulas).includes(gaugeId),
          );
          return usesGauge ? section : null;
        }),
      );
      return results.filter(Boolean);
    },
  },
};
